import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { logoutUser } from '../../store/actions/authActions';
import { describeAccess } from '../../helpers/subscription.helpers';

/**
 * Signed-in identity, plan status and the way out.
 *
 * Rendered globally rather than inside Header, because Header is only used by
 * the D-shape and horseshoe screens — a logout tucked in there would be missing
 * from most of the app.
 *
 * On /admin the menu is a session control only: who you are, back to the app,
 * and log out. Personal billing stays on the engineering screens.
 */
const HIDDEN_ON = ['/login', '/access', '/verify-email', '/verify-email-pending', '/reset-password', '/complete-profile', '/terms'];

const brand = '#c0392b';

const menuItemStyle = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  background: 'none',
  border: 'none',
  borderBottom: '1px solid #eee',
  padding: '12px 16px',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
};

const initialsOf = (user) => {
  const source = (user && (user.name || user.email)) || '';
  const parts = source.trim().split(/[\s@._-]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const AccountMenu = ({ user, isAuthenticated, access, subscription, plans, logoutUserAction }) => {
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const rootRef = useRef(null);
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    if (!open) return undefined;

    const onPointer = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Changing screen should not leave the panel hanging open over the new one.
  useEffect(() => setOpen(false), [location.pathname]);

  if (!isAuthenticated || !user) return null;
  if (HIDDEN_ON.some((p) => location.pathname.startsWith(p))) return null;

  const isAdmin = String(user.role || '').toUpperCase() === 'ADMIN';
  const onAdminScreen = location.pathname.startsWith('/admin');
  const plan = onAdminScreen ? null : describeAccess({ access, subscription, plans });

  const onSignOut = async () => {
    setSigningOut(true);
    try {
      await logoutUserAction();
    } finally {
      setSigningOut(false);
      setOpen(false);
      history.push('/login');
    }
  };

  return (
    <div
      ref={rootRef}
      style={
        onAdminScreen
          ? { position: 'fixed', top: '18px', right: '28px', zIndex: 40 }
          : { position: 'absolute', top: '14px', right: '14px', zIndex: 9000 }
      }
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={isAdmin ? 'Account, administrator' : 'Account'}
        title={user.email || 'Account'}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          border: isAdmin ? '2px solid #c0392b' : '1px solid rgba(0,0,0,0.08)',
          background: '#fff',
          color: brand,
          fontWeight: 700,
          fontSize: '13px',
          letterSpacing: '0.02em',
          cursor: 'pointer',
          boxShadow: '0 1px 6px rgba(0,0,0,0.14)',
          padding: 0,
        }}
      >
        {initialsOf(user)}
        {isAdmin && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              bottom: '-7px',
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: '8px',
              fontWeight: 800,
              letterSpacing: '0.06em',
              lineHeight: 1,
              padding: '2px 5px',
              borderRadius: '999px',
              background: brand,
              color: '#fff',
              whiteSpace: 'nowrap',
            }}
          >
            ADMIN
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: '52px',
            right: 0,
            width: '270px',
            background: '#fff',
            borderRadius: '12px',
            boxShadow: '0 8px 28px rgba(0,0,0,0.22)',
            overflow: 'hidden',
            textAlign: 'left',
          }}
        >
          <div style={{ padding: '14px 16px 12px', borderBottom: '1px solid #eee' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <div
                style={{
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#222',
                  wordBreak: 'break-word',
                }}
              >
                {user.name || 'Signed in'}
              </div>
              {isAdmin && (
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 800,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    padding: '2px 7px',
                    borderRadius: '999px',
                    background: '#fdecea',
                    color: brand,
                  }}
                >
                  Admin
                </span>
              )}
            </div>
            <div
              style={{
                fontSize: '12px',
                color: '#777',
                marginTop: '2px',
                wordBreak: 'break-all',
              }}
            >
              {user.email}
            </div>
            {!onAdminScreen && (user.organization || user.country) && (
              <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                {[user.organization, user.country].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>

          {!onAdminScreen && plan && (
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee' }}>
              <div
                style={{
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: '#999',
                  marginBottom: '6px',
                }}
              >
                Subscription
              </div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#222' }}>
                {plan.planName}
              </div>
              {plan.detail && (
                <div style={{ fontSize: '12px', color: '#777', marginTop: '4px' }}>
                  {plan.detail}
                </div>
              )}
            </div>
          )}

          {onAdminScreen ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                history.push('/');
              }}
              style={{ ...menuItemStyle, color: '#222' }}
            >
              <i className="fa fa-th-large" style={{ marginRight: '8px', color: '#999' }} />
              Back to app
            </button>
          ) : (
            <>
              {isAdmin && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    history.push('/admin');
                  }}
                  style={{ ...menuItemStyle, color: brand }}
                >
                  <i className="fa fa-shield-alt" style={{ marginRight: '8px', color: brand }} />
                  Admin Panel
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  history.push('/subscription');
                }}
                style={{ ...menuItemStyle, color: '#222' }}
              >
                <i className="fa fa-credit-card" style={{ marginRight: '8px', color: '#999' }} />
                Manage subscription
              </button>
            </>
          )}

          <button
            type="button"
            role="menuitem"
            onClick={onSignOut}
            disabled={signingOut}
            style={{
              ...menuItemStyle,
              borderBottom: 'none',
              fontWeight: 700,
              color: brand,
              cursor: signingOut ? 'default' : 'pointer',
              opacity: signingOut ? 0.6 : 1,
            }}
          >
            <i className="fa fa-sign-out-alt" style={{ marginRight: '8px' }} />
            {signingOut ? 'Logging out…' : 'Log out'}
          </button>
        </div>
      )}
    </div>
  );
};

export default connect(
  (state) => ({
    user: state.auth.user,
    isAuthenticated: state.auth.isAuthenticated,
    access: state.subscription.access,
    subscription: state.subscription.subscription,
    plans: state.subscription.plans,
  }),
  { logoutUserAction: logoutUser }
)(AccountMenu);
